import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../utils';
import { DISCHARGE_CRITERIA_FIELD_NAMES } from './constants';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { TreatmentPlanV3PlanAction, TreatmentPlanV3State } from '../../types';
import type {
  IDischargeReason,
  ITreatmentPlanV3DischargePage,
  UpdateTreatmentPlanV3DischargeCriteriaPayload,
  CreateTreatmentPlanV3DischargeReasonPayload,
  DeleteTreatmentPlanV3DischargeReasonPayload,
  TreatmentPlanV3DischargeThunks,
  DischargeMutationAction,
} from './types';

export function addTreatmentPlanV3DischargeCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3DischargeThunks,
) {
  addGetTreatmentPlanV3DischargePageCases(builder, thunks);
  addUpdateTreatmentPlanV3DischargeCriteriaCases(builder, thunks);
  addCreateTreatmentPlanV3DischargeReasonCases(builder, thunks);
  addDeleteTreatmentPlanV3DischargeReasonCases(builder, thunks);
}

function isForLoadedDischargePage(state: TreatmentPlanV3State, action: TreatmentPlanV3PlanAction) {
  return action.meta.arg.treatmentPlanId === state.dischargePageTreatmentPlanId;
}

function markDischargePageChanged(state: TreatmentPlanV3State) {
  if (state.dischargePageLoading) {
    state.dischargePageChangedSinceRequest = true;
  }
}

function addGetTreatmentPlanV3DischargePageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3DischargeThunks,
) {
  const { getTreatmentPlanV3DischargePage } = thunks;

  builder
    .addCase(getTreatmentPlanV3DischargePage.pending, (state, action) => {
      if (!isForLoadedDischargePage(state, action)) {
        state.dischargePage = null;
      }
      state.dischargePageLoading = true;
      state.dischargePageError = null;
      state.dischargePageRequestId = action.meta.requestId;
      state.dischargePageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.dischargePageChangedSinceRequest = false;
      state.error = null;
    })
    .addCase(getTreatmentPlanV3DischargePage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.dischargePageRequestId) return;
      state.dischargePageLoading = false;
      state.dischargePageError = null;
      if (!state.dischargePageChangedSinceRequest || !state.dischargePage) {
        state.dischargePage = action.payload;
        state.dischargePageLoadCount += 1;
      }
      state.dischargePageChangedSinceRequest = false;
    })
    .addCase(getTreatmentPlanV3DischargePage.rejected, (state, action) => {
      if (action.meta.requestId !== state.dischargePageRequestId) return;
      state.dischargePageLoading = false;
      state.dischargePageError = action.error;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateTreatmentPlanV3DischargeCriteriaCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3DischargeThunks,
) {
  const { updateTreatmentPlanV3DischargeCriteria } = thunks;
  const flag = 'updateDischargeCriteriaLoading';

  builder
    .addCase(updateTreatmentPlanV3DischargeCriteria.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3DischargeCriteria.fulfilled,
      (
        state,
        action: DischargeMutationAction<
          ITreatmentPlanV3DischargePage,
          UpdateTreatmentPlanV3DischargeCriteriaPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedDischargePage(state, action)) return;
        markDischargePageChanged(state);
        if (!state.dischargePage) {
          state.dischargePage = action.payload;
          return;
        }
        state.dischargePage.id = action.payload.id;
        for (const name of DISCHARGE_CRITERIA_FIELD_NAMES) {
          if (name in action.meta.arg.data) {
            state.dischargePage[name] = action.payload[name];
          }
        }
      },
    )
    .addCase(updateTreatmentPlanV3DischargeCriteria.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedDischargePage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addCreateTreatmentPlanV3DischargeReasonCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3DischargeThunks,
) {
  const { createTreatmentPlanV3DischargeReason } = thunks;
  const flag = 'createDischargeReasonLoading';

  builder
    .addCase(createTreatmentPlanV3DischargeReason.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3DischargeReason.fulfilled,
      (
        state,
        action: DischargeMutationAction<
          IDischargeReason,
          CreateTreatmentPlanV3DischargeReasonPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedDischargePage(state, action) || !state.dischargePage) return;
        markDischargePageChanged(state);
        state.dischargePage.reasons.push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3DischargeReason.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedDischargePage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

function addDeleteTreatmentPlanV3DischargeReasonCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3DischargeThunks,
) {
  const { deleteTreatmentPlanV3DischargeReason } = thunks;
  const flag = 'deleteDischargeReasonLoading';

  builder
    .addCase(deleteTreatmentPlanV3DischargeReason.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3DischargeReason.fulfilled,
      (
        state,
        action: DischargeMutationAction<void, DeleteTreatmentPlanV3DischargeReasonPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedDischargePage(state, action) || !state.dischargePage) return;
        markDischargePageChanged(state);
        state.dischargePage.reasons = state.dischargePage.reasons.filter(
          (row) => row.id !== action.meta.arg.reasonId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3DischargeReason.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedDischargePage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}
